// The audio graph (P3.7) — the things there is exactly one of, per app.
//
// One `AudioContext`, one worklet module registration, one FR-26 signal ring. Decks are built from
// it; it is not built from them.
//
// ## Why this file exists at all, since P2.1 promised deck B would be a constructor argument
//
// **P2.1's promise held, and this is the part it was not about.** `deckId` really is threaded
// through the processor, the host, the signal source and the control ids: `DeckProcessor` picks its
// source block from it (`deckId === 'b' ? DECK_B : DECK_A`) and needed no change here at all. What
// P2.1 never claimed — and what nothing before P3.7 had reason to notice — is that `Deck.start()`
// created its *own* `AudioContext`, because with one deck "the deck's context" and "the app's
// context" were the same object and nothing could tell them apart.
//
// Two decks can tell them apart immediately, and the difference is not stylistic:
//
//   * **Two contexts are two clocks.** Each has its own `currentTime` advancing off its own device
//     callback, and nothing in Web Audio relates them. FR-07's Sync aligns two decks' phase to
//     within 10 ms; two decks that cannot even name a common instant cannot be aligned to one, and
//     the drift would be unbounded rather than merely present.
//   * **Two contexts are two output devices' worth of latency**, separately negotiated. Architecture
//     §4.5's latency probe measures against the context clock, so a per-deck context would make
//     NFR-P1 two different numbers about two different things.
//   * **A browser will not give you many.** Contexts are a scarce, expensive resource; the spec
//     lets an implementation refuse. Opening one per deck is the shape that fails at four.
//
// Architecture §4 is explicit that there is one main context and that FR-19's dual-device cue is
// the exception — "a **second AudioContext** bound to the cue device". That is a second *graph*,
// which is why this is a class with an owner rather than a module singleton: M5 needs two of these
// and would have to unpick a global to get one.
//
// ## The mixer, which this file predicted (P4.2)
//
// Until M4 this header said: "**No mixer.** … the day it grows a gain node per deck is the day the
// mixer arrived without a prompt saying so." P4.2 is that prompt, and the shape it warned about is
// the one that was avoided — there is no gain node per deck. Architecture §4.2 puts the whole
// topology **inside the engine worklet**, so what the graph grew is one more `AudioWorkletNode`
// between the decks and `destination`, and everything else happens on the audio thread.
//
// The decks no longer reach `destination` at all. Each connects to its own input on the mixer, and
// the mixer owns the strips. See `mixer-processor.ts` and ADR-L13.

import { createRing } from '../../platform/sab/ring';
import { RejectRingWriter } from '../../platform/sab/spsc-ring';
import type { DeckId } from '../../platform/types/control';
import {
  MIXER_PROCESSOR,
  MIXER_STRIP_COUNT,
  type MixerProcessorOptions,
} from '../../platform/types/mixer';
import type { MixerCommand } from './control-mapping';
import { COMMAND_RECORD, COMMAND_RING, SIGNAL_RING } from '../../platform/types/rings';
import { Deck, WORKLET_MODULE_URL } from './deck';

/**
 * Ring ids, unique within this main-thread realm.
 *
 * A counter rather than a random value: this only has to distinguish the rings *one page* creates,
 * and a counter makes a report or a log readable ("ring 0" is the main graph, "ring 1" is FR-19's
 * cue bridge) where a random number would be noise.
 */
let signalsIdCounter = 0;
function nextSignalsId(): number {
  const id = signalsIdCounter;
  signalsIdCounter += 1;
  return id;
}

export class AudioGraph {
  readonly context: AudioContext;

  /**
   * FR-14's mixer — the one node every deck plays into (P4.2).
   *
   * Created in `start()` rather than lazily: it is the graph's output stage, so a deck built before
   * it existed would have nowhere to connect, and "the mixer appears when the first deck loads" is a
   * state with no benefit and one more ordering to get wrong.
   */
  readonly mixer: AudioWorkletNode;

  readonly #mixerCommands: RejectRingWriter;
  readonly #mixerShorts: Uint16Array;
  readonly #mixerFloats: Float32Array;

  /**
   * The one FR-26 signal ring, created here rather than per deck (P3.7).
   *
   * It moved out of `Deck` for the reason P3.7's rule gives: "the ring's frame carries every source
   * precisely so a deck-A and a deck-B reading describe the same block". A ring per deck would make
   * that sentence unsatisfiable — a meter and a playhead from two rings can be paired only by
   * timestamp, and by then they are two measurements that happen to be close rather than one frame.
   *
   * `SIGNAL_SOURCE` has had a block for each deck plus the master since P0.2, sized for exactly
   * this. Two writers into one ring is not what an SPSC ring does on its own, and
   * `SignalPublisher.openQuantum` is where that is made true.
   *
   * ⚠️ **Created in `start()` and passed in, rather than initialised here — P4.7 moved it.** A
   * field initialiser runs during `new AudioGraph(...)`, which is the *last* thing `start()` does;
   * the mixer node is constructed several lines earlier and now needs this ring in its
   * `processorOptions`. Left as an initialiser, the mixer would have been handed `undefined` and
   * the strips would have published into nothing — silently, because a publisher with no open frame
   * drops every write rather than throwing.
   */
  readonly signals: SharedArrayBuffer;

  /**
   * Which ring `signals` is, for the audio thread (P3.7).
   *
   * Both decks must share one `SignalPublisher` over there, and the buffer itself cannot be the key:
   * `processorOptions` is structured-cloned, so each processor receives a different JS object over
   * the same memory. A number survives that. See `sharedSignalPublisher` for the bug this exists to
   * prevent and how it was found.
   */
  readonly signalsId: number;

  #closed = false;

  private constructor(
    context: AudioContext,
    mixer: AudioWorkletNode,
    ring: SharedArrayBuffer,
    signals: SharedArrayBuffer,
    signalsId: number,
  ) {
    this.context = context;
    this.mixer = mixer;
    this.signals = signals;
    this.signalsId = signalsId;
    this.#mixerCommands = new RejectRingWriter(ring, COMMAND_RING);
    this.#mixerShorts = new Uint16Array(ring);
    this.#mixerFloats = new Float32Array(ring);
  }

  static async start(): Promise<AudioGraph> {
    if (!globalThis.crossOriginIsolated) {
      throw new Error(
        'This page is not cross-origin isolated, so SharedArrayBuffer is unavailable and the ' +
          'deck cannot start. A hard reload may be needed.',
      );
    }

    const context = new AudioContext({ latencyHint: 'interactive' });
    if (context.state === 'suspended') {
      await context.resume();
    }

    try {
      // One module, one bundle, one guard — and **one registration for both decks**, which is worth
      // a line because `audio-worklet.entry.ts` predicted otherwise. Its header expected M3's second
      // deck to arrive "as another `registerProcessor` line here"; it did not, because a processor
      // that reads its identity out of `processorOptions` is one class with two instances. The
      // multi-processor shape is still right for FR-19's cue bridge, which is a genuinely different
      // processor rather than a second copy of this one.
      await context.audioWorklet.addModule(WORKLET_MODULE_URL);
    } catch (cause) {
      await context.close();
      throw new Error(`Could not load the audio worklet from ${WORKLET_MODULE_URL}.`, {
        cause,
      });
    }

    // Stereo in and stereo out, explicitly. Left to negotiate, an input with nothing connected can
    // settle on a different channel count from one with a deck on it, and a strip that changed width
    // when a track loaded would be a bug with no symptom until it had one.
    const mixerCommandRing = createRing(COMMAND_RING);
    // Both before the mixer node, because it takes them in `processorOptions` (P4.7). The decks
    // receive the same two values from the fields below and therefore reach the same publisher.
    const signals = createRing(SIGNAL_RING);
    const signalsId = nextSignalsId();
    const mixerOptions: MixerProcessorOptions = {
      commandRing: mixerCommandRing,
      stripCount: MIXER_STRIP_COUNT,
      signals,
      signalsId,
    };
    const mixer = new AudioWorkletNode(context, MIXER_PROCESSOR, {
      numberOfInputs: MIXER_STRIP_COUNT,
      numberOfOutputs: 1,
      outputChannelCount: [2],
      channelCount: 2,
      channelCountMode: 'explicit',
      channelInterpretation: 'speakers',
      processorOptions: mixerOptions,
    });
    mixer.connect(context.destination);

    return new AudioGraph(context, mixer, mixerCommandRing, signals, signalsId);
  }

  /**
   * Writes one mixer command to the mixer's ring (P4.2, P4.10).
   *
   * Takes the shape `toMixerCommand` produces rather than named setters, and that is the same
   * decision `Deck.sendCommand` made: the mapping table is *data*, so the thing that consumes it
   * should accept the table's own output. A `setTrim(strip, gain)` API would mean the table produced
   * a command and something else immediately took it apart again — and the first control added
   * without a corresponding setter would be a control that silently could not be sent.
   *
   * Over the command ring rather than `postMessage`, and Architecture §2's split says why: a trim
   * knob or an EQ band is *continuous*, so it can absolutely perceive jitter, and the ring is the
   * path that has none. `postMessage` is for structural changes a human cannot time to the
   * millisecond.
   *
   * Returns false when the ring is full, which is `Deck.sendCommand`'s contract too: the caller is a
   * pointer drag, and dropping one frame of a gesture that is still moving beats blocking the main
   * thread on the audio thread.
   */
  sendMixerCommand(command: MixerCommand): boolean {
    if (!Number.isFinite(command.value) || command.value < 0) {
      return false;
    }
    const offset = this.#mixerCommands.claim();
    if (offset === -1) {
      return false;
    }
    this.#mixerShorts[(offset + COMMAND_RECORD.OPCODE) >>> 1] = command.opcode;
    this.#mixerShorts[(offset + COMMAND_RECORD.TARGET) >>> 1] = command.target;
    this.#mixerFloats[(offset + COMMAND_RECORD.VALUE) >>> 2] = command.value;
    this.#mixerCommands.commit();
    return true;
  }

  /**
   * A deck on this graph. Cheap and synchronous — the expensive parts (the context, the module)
   * already happened, which is exactly what makes the second deck a constructor argument.
   *
   * Creating two decks with the same id is not refused. It would be a caller bug rather than a
   * state this class can be in wrongly: both would write the same signal-source block and the last
   * one to run each block would win, which is visible immediately. A registry here to prevent it
   * would be state that has to be kept correct across every dispose, to catch a mistake that cannot
   * survive one run of the app.
   */
  createDeck(id: DeckId): Deck {
    return Deck.on(this, id);
  }

  /**
   * Closes the context. **Every deck on this graph is dead afterwards** — a deck holds nodes on a
   * closed context and can do nothing with them.
   *
   * Idempotent, because the app's teardown order is React's and effects unmount in an order this
   * class should not have opinions about.
   */
  async close(): Promise<void> {
    if (this.#closed) {
      return;
    }
    this.#closed = true;
    await this.context.close();
  }
}
